import { useState, useEffect, useContext, useRef } from "react";
import { getHoursCloseTicketsAuto } from "../../config";
import toastError from "../../errors/toastError";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useTickets = ({
    searchParam,
    pageNumber,
    status,
    date,
    showAll,
    queueIds,
    inboxIds,
    withUnreadMessages,
    onlyUnanswered,
    // A 2s polling interval mounted in the inbox, resolved and search panels
    // multiplies the load on Chatwoot/Redis. Five seconds keeps the inbox
    // responsive while avoiding a request storm on modest machines.
    refreshInterval = 5000,
}) => {
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [count, setCount] = useState(0);
    const [refreshTick, setRefreshTick] = useState(0);
    const hasLoadedTickets = useRef(false);
    const requestInFlight = useRef(false);
    const { isAuth } = useContext(AuthContext);

    useEffect(() => {
        if (!isAuth) return undefined;
        const interval = window.setInterval(() => {
            if (document.visibilityState !== "visible") return;
            if (requestInFlight.current) return;
            setRefreshTick(current => current + 1);
        }, refreshInterval);
        return () => window.clearInterval(interval);
    }, [isAuth, refreshInterval]);

    useEffect(() => {
		if (!isAuth) {
			setLoading(false);
			return undefined;
		}
		const isInitialLoad = !hasLoadedTickets.current;
		if (isInitialLoad) setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const fetchTickets = async() => {
                if (requestInFlight.current) return;
                requestInFlight.current = true;
                try {
                    const { data } = await api.get("/tickets", {
                        params: {
                            searchParam,
                            pageNumber,
                            status,
                            date,
                            showAll,
                            queueIds,
                            inboxIds,
                            withUnreadMessages,
                            onlyUnanswered,
                        },
                    })
                    setTickets(previous => {
                        const next = data.tickets || [];
                        const unchanged = previous.length === next.length && previous.every((ticket, index) => {
                            const candidate = next[index];
                            return candidate
                                && ticket.id === candidate.id
                                && ticket.updatedAt === candidate.updatedAt
                                && ticket.lastMessage === candidate.lastMessage
                                && ticket.unreadMessages === candidate.unreadMessages
                                && ticket.lastMessageFromMe === candidate.lastMessageFromMe
                                && ticket.needsReply === candidate.needsReply
                                && ticket.pendingCustomerMessages === candidate.pendingCustomerMessages
                                && ticket.status === candidate.status;
                        });
                        return unchanged ? previous : next;
                    })

                    let horasFecharAutomaticamente = getHoursCloseTicketsAuto(); 

                    if (status === "open" && horasFecharAutomaticamente && horasFecharAutomaticamente !== "" &&
                        horasFecharAutomaticamente !== "0" && Number(horasFecharAutomaticamente) > 0) {

                        let dataLimite = new Date()
                        dataLimite.setHours(dataLimite.getHours() - Number(horasFecharAutomaticamente))

                        data.tickets.forEach(ticket => {
                            if (ticket.status !== "closed") {
                                let dataUltimaInteracaoChamado = new Date(ticket.updatedAt)
                                if (dataUltimaInteracaoChamado < dataLimite)
                                    closeTicket(ticket)
                            }
                        })
                    }

                    setHasMore(data.hasMore)
                    setCount(data.count)
                    hasLoadedTickets.current = true;
                } catch (err) {
                    if (isInitialLoad) toastError(err)
                } finally {
                    requestInFlight.current = false;
                    if (isInitialLoad) setLoading(false)
                }
            }

            const closeTicket = async(ticket) => {
                await api.put(`/tickets/${ticket.id}`, {
                    status: "closed",
                    userId: ticket.userId || null,
                })
            }

            fetchTickets()
        }, 0)
        return () => clearTimeout(delayDebounceFn)
    }, [
        searchParam,
        pageNumber,
        status,
        date,
        showAll,
        queueIds,
        inboxIds,
        withUnreadMessages,
        onlyUnanswered,
		isAuth,
        refreshTick,
    ])

    return { tickets, loading, hasMore, count };
};

export default useTickets;
